import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { useLanguage } from "../context/LanguageContext";
import MarkdownContent from "../components/MarkdownContent";
import { resolveImageUrl } from "../lib/imageUrl";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const TYPE_COLORS = {
  collection: { color: "#4ade80", bg: "rgba(74,222,128,0.12)" },
  promotion: { color: "var(--ang-gold)", bg: "var(--ang-gold-soft)" },
  news: { color: "#60a5fa", bg: "rgba(96,165,250,0.12)" },
  vip: { color: "#e879f9", bg: "rgba(232,121,249,0.12)" },
};

const TYPE_MAP = { all: null, promotion: "promotion", collection: "collection", vip: "vip" };

export default function News() {
  const { t, tField } = useLanguage();
  const [news, setNews] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    axios.get(`${API}/news`).then(r => setNews(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const filtered = filter === "all" ? news : news.filter(n => n.type === TYPE_MAP[filter]);

  const FILTERS = [
    { key: "all", label: t.news.all },
    { key: "promotion", label: t.news.promos },
    { key: "collection", label: t.news.collections },
    { key: "vip", label: t.news.vip },
  ];

  const TYPE_BADGES = {
    collection: { label: t.news.collection, ...TYPE_COLORS.collection },
    promotion: { label: t.news.promotion, ...TYPE_COLORS.promotion },
    news: { label: t.news.newsItem, ...TYPE_COLORS.news },
    vip: { label: t.news.vipExclusive, ...TYPE_COLORS.vip },
  };

  return (
    <div style={{ background: "var(--ang-bg)", minHeight: "100vh" }}>
      {/* Header */}
      <div style={{ padding: "52px 20px 16px" }}>
        <h1 style={{ fontFamily: "Cormorant Garamond", fontSize: "30px", color: "var(--ang-text)", fontWeight: 400, marginBottom: "16px" }}>{t.news.title}</h1>
        <div style={{ display: "flex", gap: "8px", overflowX: "auto", paddingBottom: "4px" }}>
          {FILTERS.map(f => (
            <button key={f.key} data-testid={`news-filter-${f.key}`} onClick={() => setFilter(f.key)}
              style={{ padding: "8px 16px", borderRadius: "20px", border: `1px solid ${f.key === filter ? "var(--ang-gold)" : "var(--ang-border)"}`, background: f.key === filter ? "var(--ang-gold-soft)" : "transparent", color: f.key === filter ? "var(--ang-gold)" : "var(--ang-text-muted)", fontFamily: "Manrope", fontSize: "12px", cursor: "pointer", whiteSpace: "nowrap", fontWeight: f.key === filter ? 600 : 400 }}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* News List */}
      <div style={{ padding: "0 16px 24px" }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: "48px", color: "var(--ang-text-faint)", fontFamily: "Manrope" }}>{t.common.loading}</div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px 20px", color: "var(--ang-text-faint)", fontFamily: "Manrope", fontSize: "14px" }}>{t.news.empty}</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            {filtered.map((item, i) => {
              const badge = TYPE_BADGES[item.type] || TYPE_BADGES.news;
              const isExpanded = expanded === item.id;
              return (
                <motion.div key={item.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.06 }}
                  data-testid={`news-item-${item.id}`}
                  style={{ background: "var(--ang-surface)", borderRadius: "8px", overflow: "hidden", border: "1px solid var(--ang-border)", cursor: "pointer" }}
                  onClick={() => setExpanded(isExpanded ? null : item.id)}>
                  {item.image_url && (
                    <div style={{ width: "100%", aspectRatio: "3/4", background: "var(--ang-surface-2)", overflow: "hidden" }}>
                      <img src={resolveImageUrl(item.image_url, { w: 800 })} alt={tField(item, "title")}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        onError={e => { e.target.style.display = "none"; }} />
                    </div>
                  )}
                  <div style={{ padding: "16px" }}>
                    <div style={{ display: "flex", gap: "8px", marginBottom: "10px", flexWrap: "wrap" }}>
                      <span style={{ background: badge.bg, color: badge.color, fontFamily: "Manrope", fontSize: "10px", fontWeight: 700, padding: "3px 10px", borderRadius: "12px", letterSpacing: "0.08em" }}>{badge.label}</span>
                      {item.is_vip_only && item.type !== "vip" && (
                        <span style={{ background: "rgba(232,121,249,0.1)", color: "#e879f9", fontFamily: "Manrope", fontSize: "10px", fontWeight: 700, padding: "3px 10px", borderRadius: "12px" }}>{t.news.vip}</span>
                      )}
                    </div>
                    <h3 style={{ fontFamily: "Cormorant Garamond", fontSize: "20px", color: "var(--ang-text)", fontWeight: 400, lineHeight: 1.3, marginBottom: "8px" }}>{tField(item, "title")}</h3>
                    {isExpanded ? (
                      <MarkdownContent text={tField(item, "content") || item.content} />
                    ) : (
                      <p style={{ fontFamily: "Manrope", fontSize: "13px", color: "var(--ang-text-muted)", lineHeight: 1.6, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", margin: 0 }}>
                        {tField(item, "content") || item.content}
                      </p>
                    )}
                    <div style={{ marginTop: "12px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontFamily: "Manrope", fontSize: "11px", color: "var(--ang-border-strong)" }}>
                        {item.created_at ? new Date(item.created_at).toLocaleDateString("ru-RU", { day: "numeric", month: "long" }) : ""}
                      </span>
                      <span style={{ fontFamily: "Manrope", fontSize: "11px", color: "var(--ang-gold)" }}>
                        {isExpanded ? t.news.hide : t.news.read}
                      </span>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
